import React, {
  useContext,
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react';
import xss from 'xss';
import { PageContext } from '../frontend/pageContext';
import { ClientDocLeafMenu, ClientDocSpaceMenu } from '../types';
import {
  addRecentSearch,
  clearRecentSearches,
  getRecentSearches,
  RecentSearchEntry,
  removeRecentSearch,
} from '../util/recentSearches';

// Debounce utility - moved outside component to avoid recreation
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) & { cancel: () => void } => {
  let timeout: NodeJS.Timeout | null = null;
  const debouncedFn = (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
  debouncedFn.cancel = () => {
    if (timeout) clearTimeout(timeout);
  };
  return debouncedFn;
};

interface Props {
  menu: ClientDocSpaceMenu | ClientDocLeafMenu;
  children: JSX.Element[] | JSX.Element;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (boolean) => void;
}

interface SearchItem {
  docLeaf: string;
  title: string;
  content: string;
  url: string;
}

/**
 * A flattened row for the results list.
 */
type FlatRow =
  | { kind: 'header'; key: string; title: string }
  | { kind: 'item'; key: string; flatIndex: number; item: SearchItem };

/**
 * A flattened row for recent selected results.
 */
type RecentRow =
  | { kind: 'header'; key: string; title: string }
  | { kind: 'item'; key: string; flatIndex: number; item: RecentSearchEntry };

const SEARCH_SNIPPET_SANITIZE_OPTIONS = {
  whiteList: {
    mark: [],
    em: [],
    strong: [],
    b: [],
    i: [],
    span: ['class'],
  },
  stripIgnoreTag: true,
  stripIgnoreTagBody: ['script'],
};

const sanitizeSearchSnippet = (html: string): string =>
  xss(html, SEARCH_SNIPPET_SANITIZE_OPTIONS);

const Sidebar = ({ mobileMenuOpen, setMobileMenuOpen, children }: Props) => {
  const sideBar = useRef<HTMLElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const { isStatic, docSpace, spacePdfExists } = useContext(PageContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [resultsList, setResultsList] = useState<SearchItem[]>([]);
  const [selectedResultIndex, setSelectedResultIndex] = useState<
    number | undefined
  >();
  const [recentSearches, setRecentSearches] = useState<RecentSearchEntry[]>([]);

  // Keep docSpace in a ref to avoid stale closure in debounced function
  const docSpaceRef = useRef(docSpace);
  useEffect(() => {
    docSpaceRef.current = docSpace;
  }, [docSpace]);

  const handleClickOutside = (event) => {
    if (
      mobileMenuOpen &&
      sideBar.current &&
      !sideBar.current.contains(event.target)
    ) {
      setMobileMenuOpen(false);
      document.body.classList.remove('menu-open');
    }
  };

  useEffect(() => {
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sideBar, mobileMenuOpen]);

  // Reset selection whenever a new result set arrives. We do not
  // auto-select index 0 — Enter is a no-op until the user has arrowed onto any row.
  useEffect(() => {
    setSelectedResultIndex(undefined);
  }, [resultsList, recentSearches, searchTerm]);

  // Single close path for the popup. Always resets the query and any
  // pending debounced fetch so the popup re-opens in a clean state
  const closePopup = () => {
    setIsOpen(false);
    setSearchTerm('');
    setResultsList([]);
    debouncedSearchRef.current.cancel();
  };

  // cmd/ctrl+k opens the popup and intentionally overrides the browser default address-bar shortcut.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isOpenShortcut =
        e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey);

      if (isOpenShortcut) {
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        }
      }

      if (isOpen && e.key === 'Escape') {
        e.preventDefault();
        closePopup();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Keep the keyboard-selected row in view. Without this the `is-selected`
  // highlight scrolls out of the popup viewport when arrowing past the
  // bottom (or top) of the results list, and the user can't see what's
  // highlighted.
  useEffect(() => {
    if (selectedResultIndex === undefined || !popupRef.current) return;
    const list = popupRef.current.querySelector<HTMLElement>('.results');
    if (!list) return;
    const items = list.querySelectorAll<HTMLElement>('[data-result-index]');
    const el = list.querySelector<HTMLElement>(
      `[data-result-index="${selectedResultIndex}"]`,
    );
    if (!el) return;
    if (el === items[0]) {
      list.scrollTop = 0;
    } else if (el === items[items.length - 1]) {
      list.scrollTop = list.scrollHeight;
    } else {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedResultIndex]);

  const openKapaSearch = () => {
    (window as any).Kapa.open({ mode: 'search', query: searchTerm });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.currentTarget.value;
    setSearchTerm(text);
    debouncedSearchRef.current(text);
  };

  // Create debounced search function with access to current docSpace via ref.
  // URL-encoded so special characters (`&`, `+`, `#`, spaces, etc.) are sent
  // verbatim to the backend.
  const debouncedSearchRef = useRef(
    debounce(async (text: string) => {
      if (!text) {
        setResultsList([]);
        return;
      }

      const currentDocSpace = docSpaceRef.current;
      try {
        const res = await fetch(
          `/api/v2/search?filter=${encodeURIComponent(text)}` +
            `&document=${encodeURIComponent(currentDocSpace?.id ?? '')}` +
            `&version=${encodeURIComponent(currentDocSpace?.version ?? '')}`,
        );
        const data = await res.json();
        setResultsList(Array.isArray(data?.data) ? (data.data as SearchItem[]) : []);
      } catch (err) {
        // no-op
      }
    }, 500),
  );

  // Cleanup pending debounced calls on unmount
  useEffect(() => {
    const debouncedFn = debouncedSearchRef.current;
    return () => {
      debouncedFn.cancel();
    };
  }, []);

  // Recent selections are browser-only and scoped per docSpace. Re-read on
  // open so another tab's last write is picked up without a storage-event
  // listener.
  useEffect(() => {
    if (!isOpen) return;
    setRecentSearches(getRecentSearches(docSpaceRef.current?.id));
  }, [isOpen]);

  const recordSelectedSearch = (item: SearchItem) => {
    const currentDocSpaceId = docSpaceRef.current?.id;
    addRecentSearch(currentDocSpaceId, item, searchTerm);
    setRecentSearches(getRecentSearches(currentDocSpaceId));
  };

  const removeRecentSearchItem = (url: string) => {
    const currentDocSpaceId = docSpaceRef.current?.id;
    removeRecentSearch(currentDocSpaceId, url);
    setRecentSearches(getRecentSearches(currentDocSpaceId));
  };

  const clearRecentSearchItems = () => {
    const currentDocSpaceId = docSpaceRef.current?.id;
    clearRecentSearches(currentDocSpaceId);
    setRecentSearches([]);
  };

  // Flatten results into rows so keyboard selection uses a single linear
  // index and whole rows can be rendered as anchors. Items keep their order
  // from the API, grouped by `docLeaf` for display only.
  const flatRows = useMemo<FlatRow[]>(() => {
    const grouped: Record<string, SearchItem[]> = {};
    for (const item of resultsList) {
      const docLeafKey = item.docLeaf;
      if (!grouped[docLeafKey]) {
        grouped[docLeafKey] = [];
      }
      grouped[docLeafKey].push(item);
    }
    const rows: FlatRow[] = [];
    let idx = 0;
    for (const [title, items] of Object.entries(grouped)) {
      rows.push({ kind: 'header', key: `h:${title}`, title });
      for (const item of items) {
        rows.push({
          kind: 'item',
          key: `i:${title}:${idx}`,
          flatIndex: idx,
          item,
        });
        idx += 1;
      }
    }
    return rows;
  }, [resultsList]);

  const selectableItems = useMemo(
    () =>
      flatRows.filter(
        (r): r is Extract<FlatRow, { kind: 'item' }> => r.kind === 'item',
      ),
    [flatRows],
  );

  const recentRows = useMemo<RecentRow[]>(() => {
    const grouped: Record<string, RecentSearchEntry[]> = {};
    for (const item of recentSearches) {
      const docLeafKey = item.docLeaf;
      if (!grouped[docLeafKey]) {
        grouped[docLeafKey] = [];
      }
      grouped[docLeafKey].push(item);
    }
    const rows: RecentRow[] = [];
    let idx = 0;
    for (const [title, items] of Object.entries(grouped)) {
      rows.push({ kind: 'header', key: `recent-h:${title}`, title });
      for (const item of items) {
        rows.push({
          kind: 'item',
          key: `recent-i:${item.url}:${idx}`,
          flatIndex: idx,
          item,
        });
        idx += 1;
      }
    }
    return rows;
  }, [recentSearches]);

  const recentSelectableItems = useMemo(
    () =>
      recentRows.filter(
        (r): r is Extract<RecentRow, { kind: 'item' }> => r.kind === 'item',
      ),
    [recentRows],
  );

  const submitSearch = () => {
    if (!searchTerm.length) {
      if (selectedResultIndex !== undefined) {
        const selectedItem = recentSelectableItems[selectedResultIndex]?.item;
        if (selectedItem) {
          document.location.href = selectedItem.url;
        }
      }
      return;
    }

    if (!selectableItems.length) {
      const kapaElement = document.querySelector<HTMLElement>('.kapa-search');
      if (kapaElement) {
        openKapaSearch();
      }
      return;
    }
    if (selectedResultIndex !== undefined) {
      const selectedItem = selectableItems[selectedResultIndex].item;
      recordSelectedSearch(selectedItem);
      document.location.href = selectedItem.url;
    }
  };

  const moveSelection = (direction: 1 | -1) => {
    const len = searchTerm.length
      ? selectableItems.length
      : recentSelectableItems.length;

    if (!len) {
      return false;
    }

    const cur = selectedResultIndex ?? (direction === 1 ? -1 : 0);
    const next = (((cur + direction) % len) + len) % len;
    setSelectedResultIndex(next);
    return true;
  };

  const trapTabFocus = (e: React.KeyboardEvent) => {
    if (!popupRef.current) {
      return;
    }

    const focusable = popupRef.current.querySelectorAll<HTMLElement>(
      'a, button, input:not([disabled]):not([type="button"]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && active === last) {
      first.focus();
      e.preventDefault();
    }
  };

  const handleKeyEvents = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      submitSearch();
      return;
    }

    // While the user is inside the popup, treat Tab/Shift+Tab the same
    // as ArrowDown/ArrowUp so they can move through results without
    // leaving the keyboard's home row. If focus has moved onto a real
    // button (e.g. the × close button), the loop below takes over so
    // Tab can't accidentally jump out to the page behind the popup.
    const inPopup =
      popupRef.current?.contains(document.activeElement) ?? false;
    const isTab = e.key === 'Tab';
    const isDown =
      e.key === 'ArrowDown' || (isTab && !e.shiftKey && inPopup);
    const isUp =
      e.key === 'ArrowUp' || (isTab && e.shiftKey && inPopup);

    if (isDown || isUp) {
      if (moveSelection(isDown ? 1 : -1)) {
        e.preventDefault();
        return;
      }
    }

    if (isTab) {
      trapTabFocus(e);
    }
  };

  const renderSearchContent = () => {
    if (!searchTerm.length && recentRows.length) {
      return (
        <>
          <div className="recent-searches-toolbar">
            <span>Recent searches</span>
            <button
              type="button"
              className="recent-clear"
              onClick={clearRecentSearchItems}
              aria-label="Clear all recent searches"
            >
              Clear
            </button>
          </div>
          <div className="results recent-searches" aria-label="Recent searches">
            {recentRows.map((row) =>
              row.kind === 'header' ? (
                <div key={row.key} className="result result-group">
                  <span className="search-list" aria-hidden="true" />
                  <span className="doc-leaf">{row.title}</span>
                </div>
              ) : (
                <div
                  key={row.key}
                  className="result recent-search-item"
                >
                  <a
                    href={row.item.url}
                    data-result-index={row.flatIndex}
                    aria-current={row.flatIndex === selectedResultIndex ? true : undefined}
                    className={`leaf-content${
                      row.flatIndex === selectedResultIndex ? ' is-selected' : ''
                    }`}
                    onMouseEnter={() => setSelectedResultIndex(row.flatIndex)}
                    onKeyDown={handleKeyEvents}
                  >
                    <p
                      className="result-title"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeSearchSnippet(row.item.title),
                      }}
                    ></p>
                    <p
                      className="result-content"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeSearchSnippet(row.item.content),
                      }}
                    ></p>
                  </a>
                  <button
                    type="button"
                    className="recent-remove"
                    aria-label="Remove from recent searches"
                    onClick={() => removeRecentSearchItem(row.item.url)}
                  >
                    ×
                  </button>
                </div>
              ),
            )}
          </div>
        </>
      );
    }

    if (!searchTerm.length) {
      return (
        <div className="search-tips">
          <p>No recent searches</p>
        </div>
      );
    }

    if (flatRows.length) {
      return (
        <div className="results" aria-label="Search results">
          {flatRows.map((row) =>
            row.kind === 'header' ? (
              <div key={row.key} className="result result-group">
                <span className="search-list" aria-hidden="true" />
                <span className="doc-leaf">{row.title}</span>
              </div>
            ) : (
              <div
                key={row.key}
                className="result"
              >
                <a
                  href={row.item.url}
                  data-result-index={row.flatIndex}
                  aria-current={row.flatIndex === selectedResultIndex ? true : undefined}
                  className={`leaf-content${
                    row.flatIndex === selectedResultIndex ? ' is-selected' : ''
                  }`}
                  onMouseEnter={() => setSelectedResultIndex(row.flatIndex)}
                  onClick={() => recordSelectedSearch(row.item)}
                  onKeyDown={handleKeyEvents}
                >
                  <p
                    className="result-title"
                    dangerouslySetInnerHTML={{
                      __html: row.item.title,
                    }}
                  ></p>
                  <p
                    className="result-content"
                    dangerouslySetInnerHTML={{
                      __html: row.item.content,
                    }}
                  ></p>
                </a>
              </div>
            ),
          )}
        </div>
      );
    }

    return (
      <ul className="results">
        <li className="result kapa-search">
          <button type="button" onClick={openKapaSearch}>
            Search for &quot;{searchTerm}&quot; with KapaAi
          </button>
        </li>
      </ul>
    );
  };

  /**
   * Set the menu width and content margin when the user drags the "split bar".
   * This could certainly be implemented in a more "React-y" way, but since
   *   (1) we don't necessarily want to be super tied to React
   *   (2) we will likely replace all of this when the redesign happens
   * I decided to keep this simple for now.
   */
  const handleMenuResize = () => {
    const SIDEBAR_MIN = 320;
    const SIDEBAR_MAX = window.innerWidth * 0.3;
    const CONTENT_MIN = 200;

    const contentEl = document.querySelector<HTMLElement>('.content-area');
    const bannerEl = document.querySelector<HTMLElement>('.banners-section');
    const footerEl = document.querySelector<HTMLElement>('.suds-footer');
    const asideEl = document.querySelector<HTMLElement>('aside');

    /**
     * Sets menu width and main content left-margin as the user drags.
     */
    function resizeSidebar(ev: MouseEvent) {
      if (
        (ev.pageX > SIDEBAR_MIN || ev.pageX === SIDEBAR_MIN) &&
        ev.pageX < SIDEBAR_MAX &&
        ev.pageX < window.innerWidth - CONTENT_MIN
      ) {
        if (sideBar.current) {
          sideBar.current.style.width = `${ev.pageX}px`;
        }
        if (contentEl) {
          contentEl.style.marginLeft = `${ev.pageX}px`;
          contentEl.style.width = `${window.innerWidth - ev.pageX}px`;
        }
        if (footerEl && ev.pageX) {
          footerEl.style.width = `${window.innerWidth - ev.pageX}px`;
        }
        if (bannerEl && asideEl) {
          asideEl.style.top = `${bannerEl.getBoundingClientRect().bottom}px`;
        }
      }
    }

    /**
     * Removes event listeners when dragging is complete (mouseup).
     */
    function cleanup() {
      document.removeEventListener('mousemove', resizeSidebar);
      setTimeout(() => {
        document.removeEventListener('mouseup', cleanup);
      });
    }

    function windowResize() {
      if (window.innerWidth > 1024) {
        return resizeSidebar(
          new MouseEvent('mousemove', {
            clientX: 320,
          }),
        );
      }
    }

    document.addEventListener('mousemove', resizeSidebar);
    document.addEventListener('mouseup', cleanup);
    window.addEventListener('resize', windowResize);
  };

  return (
    <nav
      ref={sideBar}
      className={`menu nocontent${mobileMenuOpen ? ' is-open' : ''}`}
      data-test-id="desktop-search-bar"
    >
      {!isStatic && (
        <div className="search-bar">
          <input id="filter-button" className="filter-button" type="button" />
          <input
            id="search-input"
            className="search-input"
            placeholder="Search this document"
            readOnly
            onMouseDown={(e) => {
              e.preventDefault();
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
          />
          {isOpen && (
            <div className="overlay search-overlay-backdrop">
              <div
                ref={popupRef}
                className="search-overlay"
                data-test-id="search-overlay"
                role="dialog"
                aria-modal="true"
                aria-label="Search this document"
              >
                <div className="search-popup">
                  <div className="search-box">
                    <input
                      id="modal-filter-button"
                      className="modal-filter-button"
                      type="button"
                      tabIndex={-1}
                    />
                    <input
                      id="modal-search-input"
                      className="modal-search-input"
                      placeholder="Search for this document"
                      autoFocus
                      value={searchTerm}
                      type="text"
                      onChange={handleSearchChange}
                      onKeyDown={handleKeyEvents}
                    />
                    <button
                      type="button"
                      className="search-close"
                      aria-label="Close search"
                      onClick={closePopup}
                      onKeyDown={handleKeyEvents}
                    >
                      ×
                    </button>
                  </div>
                  <div
                    className={`search-site${
                      searchTerm.length ? ' open' : ''
                    }`}
                  >
                    {renderSearchContent()}
                  </div>
                  <div className="search-footer">
                    <span>
                      <kbd>↑</kbd>
                      <kbd>↓</kbd> navigate
                    </span>
                    <span>
                      <kbd>↵</kbd> open
                    </span>
                    <span>
                      <kbd>esc</kbd> close
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {children}
      {docSpace && spacePdfExists && (
        <a
          data-test-id="space-download"
          className="download-button"
          href={`/${docSpace.id}/${docSpace.semver ?? docSpace.version}/${docSpace.id}.pdf`}
          // eslint-disable-next-line react/jsx-no-target-blank
          target="_blank"
        >
          <img src="/assets/images/pdf-icon.svg" alt="pdf-icon" />
          <span>Download PDF</span>
          <span className="tooltip-text">{`Download ${docSpace.title} PDF`}</span>
        </a>
      )}
      <div
        id="split-bar"
        role="button"
        tabIndex={0}
        onMouseDown={handleMenuResize}
        onKeyDown={() => {}}
      ></div>
    </nav>
  );
};

export default Sidebar;
